/**
 * P2.5 长图竞品图切屏工具（§3.3）
 * 链路：像素行扫描找边界候选 → LLM 离散确认（切屏边界确认表）→ canvas 按选定边界精确裁切
 * 精度来源：切在「内容密度骤降带」（模块间留白）上，天然不会切开标题或图片
 * 降级：非长图返回空候选（整图模式）；解析失败由调用方回落 P2 整图参考行为
 */
import { fetchImageForCanvas } from './assetUtils';

// ---------- 纯函数（可单测） ----------

/** 长图判定：高度 ≥ 2 倍宽视为多屏拼接长图；≈9:16~9:21 单屏图不切（§3.3-1） */
export function isLongCompetitorImage(width: number, height: number): boolean {
  return width > 0 && height >= width * 2;
}

export interface BoundaryCandidate {
  /** 边界行位置（占原图高度百分比，0-100） */
  posPct: number;
  /** 置信度 0-1 */
  confidence: number;
  /** 下方内容形态标注：ok=模块起点上方（推荐）/ bad=标题分离或内部空隙（禁选/谨慎）/ mid=正文中部 / end=图尾 */
  tag?: 'ok' | 'bad' | 'mid' | 'end';
  /** 标注说明（随候选清单透出给 LLM） */
  note?: string;
}

export interface DetectBoundaryOptions {
  /** 通道一空带最小行数上限：超长图上比例式 minBand 失效，调用方按原图宽换算传入（≈32px 原图的行数） */
  minBandCap?: number;
}

const QUANT_BITS = 4;
const QUANT_SHIFT = 8 - QUANT_BITS;
const BUCKET_COUNT = 1 << (QUANT_BITS * 3);
/** 与背景色的逐通道和距离超过该值视为非背景像素（抗 JPEG 噪声与抗锯齿） */
const BG_DIST_THRESHOLD = 60;
/** 行众数占比达到该值才认定行众数即背景（防全内容行误判） */
const ROW_DOMINANT_MIN = 0.55;
/** 渐变修正：水平跳变可解释的最大内容宽度系数（跳变数/行宽 × 该值 作为回退计数的上界） */
const JUMP_EXTENT_SCALE = 40;
/** 通道二深谷带最小行数上限（比例式在超长图上失效：19282px 高图 0.15% ≈ 23 行，而真实屏间硬切谷带仅 2~10 行） */
const MIN_BAND2_CAP = 8;
/** 通道一空带最小行数按原图像素换算的封顶（真实模块留白通常 20~40px，超长图比例式膨胀到 45+ 行导致全部漏检） */
const MIN_BAND_ORIG_PX = 32;

function bucketKey(r: number, g: number, b: number): number {
  return ((r >> QUANT_SHIFT) << (QUANT_BITS * 2)) | ((g >> QUANT_SHIFT) << QUANT_BITS) | (b >> QUANT_SHIFT);
}

/** 量化桶中心代表色（反解量化值并加半档偏移） */
function bucketColor(key: number): { r: number; g: number; b: number } {
  const half = 1 << (QUANT_SHIFT - 1);
  const r = ((key >> (QUANT_BITS * 2)) << QUANT_SHIFT) + half;
  const g = (((key >> QUANT_BITS) & ((1 << QUANT_BITS) - 1)) << QUANT_SHIFT) + half;
  const b = ((key & ((1 << QUANT_BITS) - 1)) << QUANT_SHIFT) + half;
  return { r, g, b };
}

function colorDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
  return Math.abs(r1 - r2) + Math.abs(g1 - g2) + Math.abs(b1 - b2);
}

/**
 * 逐行计算非背景像素占比（§3.3-2）。
 * 背景色自适应：行众数占比 ≥ 55% 时以行众数为背景；
 * 否则回退全图众数背景，并以水平跳变数上界收缩（渐变自适应修正）：
 * 横向渐变行内无众数，远端渐变色会被全图众数误计为内容致整行密度虚高，
 * 而平滑渐变行相邻像素几乎无跳变 → 真实内容宽度必然受限，据此收缩；
 * 量化噪声相邻桶心距 ≤48 < BG_DIST_THRESHOLD，不会误计为跳变。
 */
export function computeRowNonBgRatios(data: Uint8ClampedArray, width: number, height: number): Float32Array {
  const ratios = new Float32Array(height);
  if (width <= 0 || height <= 0) return ratios;

  const buckets = new Uint32Array(BUCKET_COUNT);
  // 全图众数背景
  for (let i = 0; i < width * height; i++) {
    buckets[bucketKey(data[i * 4], data[i * 4 + 1], data[i * 4 + 2])]++;
  }
  let gKey = 0;
  let gMax = 0;
  for (let k = 0; k < BUCKET_COUNT; k++) {
    if (buckets[k] > gMax) { gMax = buckets[k]; gKey = k; }
  }
  const gb = bucketColor(gKey);

  for (let y = 0; y < height; y++) {
    buckets.fill(0);
    const rowStart = y * width * 4;
    for (let x = 0; x < width; x++) {
      const i = rowStart + x * 4;
      buckets[bucketKey(data[i], data[i + 1], data[i + 2])]++;
    }
    let rKey = 0;
    let rMax = 0;
    for (let k = 0; k < BUCKET_COUNT; k++) {
      if (buckets[k] > rMax) { rMax = buckets[k]; rKey = k; }
    }
    const domRatio = rMax / width;
    let nonBg: number;
    if (domRatio >= ROW_DOMINANT_MIN) {
      nonBg = 1 - domRatio;
    } else {
      let count = 0;
      for (let x = 0; x < width; x++) {
        const i = rowStart + x * 4;
        if (colorDistance(data[i], data[i + 1], data[i + 2], gb.r, gb.g, gb.b) > BG_DIST_THRESHOLD) count++;
      }
      let jumps = 0;
      for (let x = 1; x < width; x++) {
        const i = rowStart + x * 4;
        const p = i - 4;
        if (colorDistance(data[i], data[i + 1], data[i + 2], data[p], data[p + 1], data[p + 2]) > BG_DIST_THRESHOLD) jumps++;
      }
      nonBg = Math.min(count / width, (jumps / width) * JUMP_EXTENT_SCALE);
    }
    ratios[y] = nonBg;
  }
  return ratios;
}

/** 行均色采样：逐行 RGB 均值（通道三输入，检测满铺硬切的底色信号） */
export interface RowColorSample {
  r: number;
  g: number;
  b: number;
}

/** 逐行计算 RGB 均值（透明像素按原值计入，扫描画布已填白底） */
export function computeRowMeanColors(data: Uint8ClampedArray, width: number, height: number): RowColorSample[] {
  if (width <= 0 || height <= 0) return [];
  const rows: RowColorSample[] = new Array(height);
  for (let y = 0; y < height; y++) {
    let rs = 0;
    let gs = 0;
    let bs = 0;
    const rowStart = y * width * 4;
    for (let x = 0; x < width; x++) {
      const i = rowStart + x * 4;
      rs += data[i];
      gs += data[i + 1];
      bs += data[i + 2];
    }
    rows[y] = { r: rs / width, g: gs / width, b: bs / width };
  }
  return rows;
}

/** 下方内容块判定的内容密度门槛（低于视为空白/过渡） */
const BELOW_CONTENT = 0.12;
/** 下方内容块聚合时允许的空隙行数（毛刺容差） */
const BELOW_NOISE = 2;

/** 从 band 下缘向下找首个连续内容块（容忍 ≤BELOW_NOISE 行毛刺空隙），返回块范围与峰值密度 */
function firstBlockBelow(smooth: Float32Array, start: number): { from: number; to: number; peak: number } | null {
  const H = smooth.length;
  let y = start;
  while (y < H && smooth[y] < BELOW_CONTENT) y++;
  if (y >= H) return null;
  let from = y;
  let to = y;
  let gap = 0;
  for (let z = y + 1; z < H; z++) {
    if (smooth[z] >= BELOW_CONTENT) {
      from = Math.min(from, z);
      to = Math.max(to, z);
      gap = 0;
    } else if (++gap > BELOW_NOISE) break;
  }
  let peak = 0;
  for (let j = from; j <= to; j++) peak = Math.max(peak, smooth[j]);
  return { from, to, peak };
}

/**
 * 通道一候选形态标注（标题-内容分离防护）。
 * 空白带本身区分不了「模块起点上方留白」（好切点）与「标题-正文间空隙」（坏切点），
 * 需结合上下两侧内容形态判定：
 * - ok：下方为满宽装饰色带（模块分隔），或上一模块内容紧邻带上方结束（新模块标题/装饰起点）
 * - bad：下方直接是正文大图且上方近处无满宽内容（标题疑被切留在上一屏），禁选；
 *        或两侧均为低密度文字/过渡区（疑为模块内部空隙），谨慎
 * - mid：下方为正文大图且上一模块刚满宽收尾（模块中部，仅作粗切参考）
 * - end：图尾留白
 */
function annotateBand(smooth: Float32Array, bandFrom: number, bandEnd: number): { tag: BoundaryCandidate['tag']; note: string } {
  const H = smooth.length;
  const ribbonH = Math.max(3, Math.round(H * 0.008));
  const win = Math.round(H * 0.003);
  const below = firstBlockBelow(smooth, bandEnd + 1);
  let abovePeak = 0;
  for (let j = Math.max(0, bandFrom - win); j < bandFrom; j++) abovePeak = Math.max(abovePeak, smooth[j]);
  if (!below) return { tag: 'end', note: '图尾留白' };
  const blockH = below.to - below.from + 1;
  if (blockH <= ribbonH && below.peak >= 0.9) {
    return { tag: 'ok', note: '下方为满宽装饰色带（模块分隔），推荐' };
  }
  if (below.peak >= 0.85) {
    if (abovePeak < 0.9) {
      return { tag: 'bad', note: '下方直接是正文大图，上方文字疑为被分离的模块标题，禁止选此行' };
    }
    return { tag: 'mid', note: '下方为正文大图（处于模块中部）' };
  }
  if (abovePeak >= 0.5) {
    return { tag: 'ok', note: '上一模块到此结束，下方为新模块区域起点，推荐' };
  }
  // 形态判定边界：真模块边界（淡色标题装饰区上方）与模块内部空隙的低密度特征重叠，纯形态无法区分，
  // 文案中性化交由 LLM 对照画面裁决（下方紧邻新模块开始标志则选，模块图文中间则不选）
  return { tag: 'bad', note: '两侧低密度过渡区，须对照画面判断：下方为新模块标题/装饰起点则选，处于模块图文中间则禁选' };
}

/**
 * 内容密度骤降带检测：从行密度序列提取边界候选行（§3.3-2）。
 * 通道一（绝对空带）：连续「空行带」（密度 < 0.05）→ 常规留白分界，附下方内容形态标注（annotateBand）；
 * 通道二（相对深谷）：谷底密度显著低于两侧基准 → 满铺长图的屏间硬切分界（屏间无留白）；
 * 通道三（行均色突变，需传 rowColors）：相邻行底色达锐度线突变 → 满铺色块分界
 * （两侧均满密度、无密度谷，仅底色切换；缓渐变带缘无相邻行色差突变，不触发）。
 * 仅认「窄锐线 + 至少一侧纯色带」：照片纹理内部行均色持续大幅波动（连续多行突变、
 * 两侧均无纯色带），全部排除——否则满铺长图上色突变假阳性会打满候选上限、
 * 淹没留白/深谷真边界（微信长截图实测 40 条候选近全为照片纹理误报）。
 * 互斥规则分道：深谷无独立证据（留白处谷底 rel 恒高），与任一空带重叠即跳过；
 * 色突变是独立强证据，仅与「产出了候选」的空带互斥——不达 minBand 的窄留白带
 * 不得吞并其附近的强色突变分界（超长图上窄带两侧恰是高频真实屏界）。
 */
export function detectBoundaryCandidates(
  rowRatios: Float32Array,
  rowColors?: RowColorSample[],
  options?: DetectBoundaryOptions,
): BoundaryCandidate[] {
  const H = rowRatios.length;
  if (H < 20) return [];

  // 窗口 3 均值平滑（抗单行噪声）
  const smooth = new Float32Array(H);
  for (let i = 0; i < H; i++) {
    smooth[i] = (rowRatios[Math.max(0, i - 1)] + rowRatios[i] + rowRatios[Math.min(H - 1, i + 1)]) / 3;
  }

  const EMPTY = 0.05;
  const CONTEXT = 8;
  const raw: BoundaryCandidate[] = [];
  const allEmptyBands: Array<[number, number]> = [];
  const candidateBands: Array<[number, number]> = [];

  // 通道一：绝对空带
  const minBand = Math.min(Math.max(2, Math.round(H * 0.003)), Math.max(2, options?.minBandCap ?? Infinity));
  let bandStart = -1;
  for (let i = 0; i <= H; i++) {
    const isEmpty = i < H && smooth[i] < EMPTY;
    if (isEmpty && bandStart < 0) bandStart = i;
    if (!isEmpty && bandStart >= 0) {
      const bandEnd = i - 1;
      const bandFrom = bandStart;
      const bandLen = bandEnd - bandFrom + 1;
      const center = (bandFrom + bandEnd) / 2;
      bandStart = -1;
      allEmptyBands.push([bandFrom, bandEnd]);
      if (bandLen < minBand) continue;
      const posPct = (center / H) * 100;
      if (posPct < 2 || posPct > 98) continue;
      // 两侧内容密度：一侧无内容说明是图首尾留白而非模块分界
      let aboveSum = 0;
      let aboveN = 0;
      for (let j = Math.max(0, bandFrom - CONTEXT); j < bandFrom; j++) { aboveSum += smooth[j]; aboveN++; }
      let belowSum = 0;
      let belowN = 0;
      for (let j = bandEnd + 1; j <= Math.min(H - 1, bandEnd + CONTEXT); j++) { belowSum += smooth[j]; belowN++; }
      if (aboveN === 0 || belowN === 0) continue;
      const contrast = Math.min(aboveSum / aboveN, belowSum / belowN);
      // 弱对比带保留：淡色标题装饰区上方留白 contrast 可低至 0.05，恰是标题分离防护的关键候选，
      // 一律产出候选，好坏交由 annotateBand 形态标注区分，contrast 仅作置信度排序信号
      const confidence = Math.min(1, Math.max(contrast, 0.05) * 1.4 + Math.min(bandLen / (H * 0.02), 1) * 0.25);
      const { tag, note } = annotateBand(smooth, bandFrom, bandEnd);
      candidateBands.push([bandFrom, bandEnd]);
      raw.push({ posPct, confidence, tag, note });
    }
  }

  // 通道二：相对深谷（满铺长图屏间硬切：无空行带，但谷底密度显著低于两侧基准）
  {
    const REL_DROP = 0.1;
    const MIN_DROP = 0.05;
    const BASE_MIN = 0.18;
    const WIDE = Math.max(20, Math.round(H * 0.006));
    const EDGE = Math.max(4, Math.round(H * 0.0008));
    const minBand2 = Math.min(Math.max(2, Math.round(H * 0.0015)), MIN_BAND2_CAP);
    // 前缀和加速任意窗口均值
    const prefix = new Float64Array(H + 1);
    for (let i = 0; i < H; i++) prefix[i + 1] = prefix[i] + smooth[i];
    const windowMean = (lo: number, hi: number): number | null => {
      const l = Math.max(0, lo);
      const r = Math.min(H, hi);
      return r - l > 0 ? (prefix[r] - prefix[l]) / (r - l) : null;
    };
    const deep = new Uint8Array(H);
    for (let y = 0; y < H; y++) {
      const left = windowMean(y - WIDE, y - EDGE);
      const right = windowMean(y + EDGE + 1, y + WIDE + 1);
      const base = left !== null && right !== null ? Math.min(left, right) : left ?? right;
      if (base === null) continue;
      if (base - smooth[y] >= Math.max(MIN_DROP, base * REL_DROP)) deep[y] = 1;
    }
    let y = 0;
    while (y < H) {
      if (!deep[y]) { y++; continue; }
      const bandFrom = y;
      while (y + 1 < H && deep[y + 1]) y++;
      const bandEnd = y;
      y++;
      // 与任一空带重叠的谷带视为同一分界：深谷无独立证据（留白处 rel 恒高），全部归通道一管
      if (allEmptyBands.some(([b0, b1]) => bandFrom <= b1 + 1 && bandEnd >= b0 - 1)) continue;
      const bandLen = bandEnd - bandFrom + 1;
      const posPct = ((bandFrom + bandEnd) / 2 / H) * 100;
      if (bandLen < minBand2 || posPct < 2 || posPct > 98) continue;
      let valley = Infinity;
      for (let j = bandFrom; j <= bandEnd; j++) if (smooth[j] < valley) valley = smooth[j];
      const above = windowMean(bandFrom - WIDE, bandFrom);
      const below = windowMean(bandEnd + 1, bandEnd + 1 + WIDE);
      const base = above !== null && below !== null ? Math.min(above, below) : above ?? below;
      // 基准过低说明是整体稀疏区（如参数表区）而非屏间分界
      if (base === null || base < BASE_MIN) continue;
      const rel = (base - valley) / Math.max(base, 1e-6);
      if (rel <= 0) continue;
      const confidence = Math.min(1, rel * 0.8 + 0.1);
      raw.push({ posPct, confidence });
    }
  }

  // 通道三：行均色突变（满铺色块硬切分界）
  if (rowColors && rowColors.length === H) {
    const SHARP = 80;
    const BASE_MIN = 0.05;
    /** 真硬切仅 1~3 行锐线（带抗锯齿渐变最多 5~6 行）；照片纹理连续多行突变，超宽即排除 */
    const MAX_SHARP_BAND = 6;
    /** 纯色带佐证：突变线该侧紧邻 STABLE_SPAN 行行均色波动 ≤ STABLE_TOL 视为纯色带 */
    const STABLE_SPAN = 12;
    const STABLE_TOL = 30;
    const WIDE = Math.max(20, Math.round(H * 0.006));
    const prefix = new Float64Array(H + 1);
    for (let i = 0; i < H; i++) prefix[i + 1] = prefix[i] + smooth[i];
    const windowMean = (lo: number, hi: number): number | null => {
      const l = Math.max(0, lo);
      const r = Math.min(H, hi);
      return r - l > 0 ? (prefix[r] - prefix[l]) / (r - l) : null;
    };
    const colorJump = (y: number): number =>
      colorDistance(rowColors[y].r, rowColors[y].g, rowColors[y].b, rowColors[y + 1].r, rowColors[y + 1].g, rowColors[y + 1].b);
    const rowColorDist = (a: RowColorSample, b: RowColorSample): number =>
      colorDistance(a.r, a.g, a.b, b.r, b.g, b.b);
    let y = 0;
    while (y < H - 1) {
      if (colorJump(y) < SHARP) { y++; continue; }
      const bandFrom = y;
      while (y + 1 < H - 1 && colorJump(y + 1) >= SHARP) y++;
      const bandEnd = y;
      y++;
      // 仅与产出了候选的空带互斥：不达 minBand 的窄留白带不得吞并强色突变分界
      if (candidateBands.some(([b0, b1]) => bandFrom <= b1 + 1 && bandEnd >= b0 - 1)) continue;
      // 照片纹理排除一：连续突变行过多（真硬切锐线不超过数行）
      if (bandEnd - bandFrom + 1 > MAX_SHARP_BAND) continue;
      // 照片纹理排除二：两侧均无纯色带（紧邻行均色持续波动）说明是照片内部底色切换
      let stableAbove = true;
      for (let j = Math.max(0, bandFrom - STABLE_SPAN); j < bandFrom; j++) {
        if (rowColorDist(rowColors[j], rowColors[bandFrom]) > STABLE_TOL) { stableAbove = false; break; }
      }
      let stableBelow = true;
      const belowRef = bandEnd + 1;
      for (let j = belowRef + 1; j <= Math.min(H - 1, belowRef + STABLE_SPAN); j++) {
        if (rowColorDist(rowColors[j], rowColors[belowRef]) > STABLE_TOL) { stableBelow = false; break; }
      }
      if (!stableAbove && !stableBelow) continue;
      // 位置取突变带首行（硬切线在其下缘）；多行突变（细渐变分隔带）取带中点
      const center = (bandFrom + bandEnd) / 2;
      const posPct = (center / H) * 100;
      if (posPct < 2 || posPct > 98) continue;
      // 满铺色块分界两侧常为稀疏区（深底模块/大留白），基准线需远低于通道二
      const above = windowMean(bandFrom - WIDE, bandFrom);
      const below = windowMean(bandEnd + 2, bandEnd + 2 + WIDE);
      const base = above !== null && below !== null ? Math.min(above, below) : above ?? below;
      if (base === null || base < BASE_MIN) continue;
      let peak = 0;
      for (let j = bandFrom; j <= bandEnd; j++) peak = Math.max(peak, colorJump(j));
      // 无留白的弱证据：整体压低于空带/深谷（候选截断、相邻合并与 LLM 排序时真留白优先）
      const confidence = Math.min(1, 0.4 + peak / 500) * 0.75;
      // 形态标注：满铺设计图上通道三是主要边界来源，纯「未标注」会让 LLM 无从取舍
      // （实测 40 条候选 37 条未标注，LLM 只敢选 2 条✓推荐致 7 模块并一屏）
      let tag: BoundaryCandidate['tag'] | undefined;
      let note: string;
      if (stableAbove && stableBelow) {
        tag = 'ok';
        note = '两侧均为纯色带（满宽装饰分隔带，模块分隔），推荐';
      } else if (stableAbove) {
        // 上方纯色块结束、下方内容起始：新模块起点常见形态，交 LLM 对照画面确认
        const belowThin = below !== null && above !== null && below < above * 0.5;
        note = belowThin
          ? '上方为纯色块结束、下方为浅色区起始（新模块标题区常见形态），请对照画面确认'
          : '上方为纯色块结束、下方为内容起始，请对照画面确认是否新模块起点';
      } else {
        // 上方内容结束、下方纯色带起始：疑为装饰分隔带（选此线则下一屏以色带开头）
        note = '上方内容结束、下方为纯色带起始（疑为装饰分隔带），请对照画面确认';
      }
      raw.push({ posPct, confidence, tag, note });
    }
  }

  // 相邻候选合并（间距 < 0.6%）：形态标注优先（ok 胜过 bad/mid/无标注），同级取置信度高者；
  // 0.6% 以外的近邻共存，交由 LLM 判断
  const rankOf = (c: BoundaryCandidate): number => (c.tag === 'ok' ? 1 : 0);
  raw.sort((a, b) => a.posPct - b.posPct);
  const merged: BoundaryCandidate[] = [];
  for (const cand of raw) {
    const prev = merged[merged.length - 1];
    if (prev && cand.posPct - prev.posPct < 0.6) {
      if (rankOf(cand) > rankOf(prev) || (rankOf(cand) === rankOf(prev) && cand.confidence > prev.confidence)) {
        merged[merged.length - 1] = cand;
      }
    } else {
      merged.push(cand);
    }
  }

  // 上限保护：优先保留 ok 标注，同级按置信度降序截断后恢复位置序
  const MAX_CANDIDATES = 40;
  const trimmed = merged.length > MAX_CANDIDATES
    ? [...merged].sort((a, b) => rankOf(b) - rankOf(a) || b.confidence - a.confidence).slice(0, MAX_CANDIDATES).sort((a, b) => a.posPct - b.posPct)
    : merged;
  return trimmed;
}

// ---------- 异步编排 ----------

export interface CompetitorScanResult {
  isLong: boolean;
  width: number;
  height: number;
  candidates: BoundaryCandidate[];
}

/**
 * 按 URL 顺序找第一张多屏长图。
 * 启动侧（扫描候选行）与裁切侧（确认表落库）共用此判定与顺序，保证两侧指向同一张图。
 */
export async function findFirstLongImageUrl(imageUrls: string[]): Promise<string | null> {
  for (const url of imageUrls) {
    try {
      const img = await fetchImageForCanvas(url);
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (isLongCompetitorImage(w, h)) return url;
    } catch {
      // 单图载入失败跳过，继续找下一张
    }
  }
  return null;
}

/** 扫描行宽（512px 足以识别留白带，控制计算量） */
const SCAN_WIDTH = 512;

/**
 * 扫描竞品图：长图判定 + 边界候选行清单（§3.3-1/2）。
 * 非长图（单屏图）isLong=false、候选为空，调用方回落整图模式。
 */
export async function scanCompetitorImage(imageUrl: string): Promise<CompetitorScanResult> {
  const img = await fetchImageForCanvas(imageUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  const isLong = isLongCompetitorImage(width, height);
  if (!isLong) {
    return { isLong: false, width, height, candidates: [] };
  }

  const scanH = Math.max(1, Math.round((height / width) * SCAN_WIDTH));
  const canvas = document.createElement('canvas');
  canvas.width = SCAN_WIDTH;
  canvas.height = scanH;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, SCAN_WIDTH, scanH);
  const imageData = ctx.getImageData(0, 0, SCAN_WIDTH, scanH);
  const rowRatios = computeRowNonBgRatios(imageData.data, SCAN_WIDTH, scanH);
  const rowColors = computeRowMeanColors(imageData.data, SCAN_WIDTH, scanH);
  const candidates = detectBoundaryCandidates(rowRatios, rowColors, {
    minBandCap: Math.round((MIN_BAND_ORIG_PX * SCAN_WIDTH) / width),
  });
  return { isLong: true, width, height, candidates };
}

/** 浏览器单画布高度安全上限（超出则先等比缩小再裁切） */
const MAX_CROP_HEIGHT = 16384;

/**
 * 按选定边界裁切竞品长图为屏切块（§3.3-4）。
 * cutPcts 为 LLM 确认表中选定的边界行位置%（去重排序后使用），
 * 产出 cuts.length + 1 个 dataURL（jpeg 0.92），由调用方上传落库。
 */
export async function cropCompetitorScreens(imageUrl: string, cutPcts: number[]): Promise<string[]> {
  const img = await fetchImageForCanvas(imageUrl);
  const origW = img.naturalWidth || img.width;
  const origH = img.naturalHeight || img.height;
  const scale = origH > MAX_CROP_HEIGHT ? MAX_CROP_HEIGHT / origH : 1;
  const dstW = Math.max(1, Math.round(origW * scale));

  const cuts = [...new Set(cutPcts)].filter((p) => p > 0 && p < 100).sort((a, b) => a - b);
  const edges = [0, ...cuts, 100];
  const dataUrls: string[] = [];
  for (let i = 0; i < edges.length - 1; i++) {
    // 源区域始终按原图坐标取，目标按缩放系数落画布
    const srcTop = Math.round((origH * edges[i]) / 100);
    const srcBottom = Math.round((origH * edges[i + 1]) / 100);
    const srcH = srcBottom - srcTop;
    const dstH = Math.max(1, Math.round(srcH * scale));
    if (srcH <= 0) continue;
    const canvas = document.createElement('canvas');
    canvas.width = dstW;
    canvas.height = dstH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.drawImage(img, 0, srcTop, origW, srcH, 0, 0, dstW, dstH);
    dataUrls.push(canvas.toDataURL('image/jpeg', 0.92));
  }
  return dataUrls;
}
